// Modal — reusable dialog shell mirroring AddSupplementModal's Backdrop +
// FormShell pattern. ESC closes; backdrop click closes; clicks inside the
// inner card don't bubble. Mobile-first: full-width sheet at the bottom on
// small screens, centered card on desktop.

import { useEffect } from 'react';
import type { ReactNode } from 'react';

export function Modal({
  title,
  open,
  onClose,
  children,
  footer,
}: {
  title: string;
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
      className="fixed inset-0 z-30 bg-fg/25 backdrop-blur-md flex items-end sm:items-center justify-center p-4"
      onClick={onClose}
    >
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-sm">
        {/* The sheet is a white card lifted off the page — the radius and the
            deeper modal shadow both come from CSS (`.surface-raised` +
            `.surface-modal`), so no rounded-* / shadow-* utility here: an
            unlayered material beats any utility written beside it. */}
        <div className="surface-raised surface-modal p-5 space-y-3 max-h-[85vh] overflow-y-auto">
          <header className="flex items-baseline justify-between sticky top-0 bg-bg-raised -m-5 mb-0 px-5 pt-5 pb-3 z-10">
            <h2 id="modal-title" className="font-display text-2xl">
              {title}
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="font-mono text-[10px] uppercase tracking-[0.16em] text-fg-faint hover:text-fg transition-colors"
            >
              Close
            </button>
          </header>
          <div className="space-y-3">{children}</div>
          {footer && <div className="pt-2">{footer}</div>}
        </div>
      </div>
    </div>
  );
}
